// signals-web.ts — a FAITHFUL port of src-tauri/src/signals.rs `compute()` for the web build.
//
// 🔴 WHY THIS EXISTS (ledger row 2c). `compute_signals` in web-commands.ts was a ZERO STUB
// (`// TODO: port derived logic to JS`). The damage was double, and both halves are invisible
// unless you actually run the feature:
//   1. on web the Weekly Recap could NEVER work — it answered "Log a few chores done first,
//      there's nothing to recap yet" with a full week of completions on screen;
//   2. worse, the BILLED AI CALL was handed an empty object, so we charged a model to describe
//      data it could not see.
// Caught 2026-07-29 by running the recap button for real. `gate-check` was 27/27 green throughout
// — a stub is valid code, so no static check can see it. Only running the feature can.
//
// 🔴 THE RUST WINS ON ANY DRIFT. Native macOS/iOS run signals.rs; this runs on web. The same
// person on two devices must see the SAME figures, so every rule below mirrors the Rust exactly:
// the same window arithmetic, the same sort orders, the same tie-breaks, the same sentence
// wording. If you change one, change both.
//
// ⚠️ `schema` is copied VERBATIM from signals.rs. It is the model's INSTRUCTION ENVELOPE, not a
// version tag — it tells the brain what each block means and what it must never say. The stub had
// shortened it to "chores.v1", which silently gave the web surface a different AI contract.

export type Tone = "neutral" | "active" | "quiet";
export type FactKind = "period" | "top" | "least" | "streak" | "span";

export interface HeadlineFact { kind: FactKind; text: string; tone: Tone }

export interface ChoreTallyW {
  chore_id: string; name: string; cadence: string; points_each: number; active: boolean;
  total_count: number; total_points: number; period_count: number; last_done_on: string | null;
}

export interface MemberSignalsW {
  schema: string;
  generated_for: string;
  member_name: string;
  period_days: number;
  overall: {
    total_completions: number; total_points: number; active_chores: number;
    first_logged: string | null; last_logged: string | null; days_span: number; unit_note: string;
  };
  period: {
    from: string; to: string; completions: number; points: number;
    active_days: number; distinct_chores: number;
  };
  per_chore: ChoreTallyW[];
  streak: { current_days: number; anchor_day: string | null; counts_from_today: boolean; longest_days: number };
  headline_facts: HeadlineFact[];
}

// Verbatim from signals.rs — do not shorten, do not paraphrase.
const SCHEMA =
  "oriora.chores.v1-signals: derived household-chore TALLY for one member (no raw notes). " +
  "Every figure is a count of chores logged, a points sum, a date, or arithmetic between them (a streak of consecutive logged days). " +
  "period covers the recap window (default last 7 days); per_chore is each chore's all-time + in-window count (sorted most-done first); " +
  "streak is the run of consecutive calendar days with at least one logged chore; " +
  "headline_facts is a pre-ranked list of ready-to-say observational sentences already containing the figures — LEAD with these, weave them into a short plain-words recap, do not re-derive numbers. " +
  "There is NO judgement of the member, NO 'is this enough', NO reward/punishment, NO advice on handling another member anywhere — it is a tally of what was logged. " +
  "Narrate the figures as plain observations; never say a count is good/bad/enough, never recommend a reward or consequence, never advise anyone on how to manage or handle another member.";

const DAY = 86_400_000;
const s = (n: number) => (n === 1 ? "" : "s");
/** Local-midnight day index — mirrors the Rust NaiveDate arithmetic, never UTC. */
function dayIdx(ymd: string): number {
  const [y, m, d] = ymd.slice(0, 10).split("-").map(Number);
  return Math.floor(new Date(y, (m ?? 1) - 1, d ?? 1).getTime() / DAY);
}
function idxToYmd(i: number): string {
  const d = new Date(i * DAY);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

interface CompIn { member_id: string; chore_id: string; done_on: string; points: number }
interface ChoreIn { id: string; member_id: string; name: string; cadence: string; points: number; active: number }
interface MemberIn { id: string; name: string }

export function computeSignalsWeb(
  member: MemberIn,
  chores: ChoreIn[],
  completions: CompIn[],
  todayYmd: string,
  periodDays: number,
): MemberSignalsW {
  const window = periodDays > 0 ? periodDays : 7;
  const today = dayIdx(todayYmd);
  // Rust: window is INCLUSIVE of today, so `from` is today - (window - 1).
  const from = today - (window - 1);

  const mine = completions.filter((c) => c.member_id === member.id);
  const myChores = chores.filter((c) => c.member_id === member.id);

  // ── overall (all-time, this member) ───────────────────────────────────────────
  const days = mine.map((c) => dayIdx(c.done_on)).sort((a, b) => a - b);
  const totalPoints = mine.reduce((a, c) => a + (c.points || 0), 0);
  const firstIdx = days.length ? days[0] : null;
  const lastIdx = days.length ? days[days.length - 1] : null;

  // ── period slice ──────────────────────────────────────────────────────────────
  const inWin = mine.filter((c) => { const i = dayIdx(c.done_on); return i >= from && i <= today; });
  const winDays = new Set(inWin.map((c) => dayIdx(c.done_on)));

  // ── per-chore, sorted MOST-DONE FIRST then name (mirrors the Rust sort) ────────
  const per_chore: ChoreTallyW[] = myChores.map((ch) => {
    const all = mine.filter((c) => c.chore_id === ch.id);
    const win = inWin.filter((c) => c.chore_id === ch.id);
    const lastDone = all.length
      ? idxToYmd(Math.max(...all.map((c) => dayIdx(c.done_on))))
      : null;
    return {
      chore_id: ch.id, name: ch.name, cadence: ch.cadence, points_each: ch.points,
      active: ch.active !== 0,
      total_count: all.length,
      total_points: all.reduce((a, c) => a + (c.points || 0), 0),
      period_count: win.length,
      last_done_on: lastDone,
    };
  }).sort((a, b) => b.total_count - a.total_count || a.name.localeCompare(b.name));

  // ── streak: runs of consecutive logged days ───────────────────────────────────
  const uniq = [...new Set(days)].sort((a, b) => a - b);
  let longest = 0, run = 0;
  for (let i = 0; i < uniq.length; i++) {
    run = i > 0 && uniq[i] === uniq[i - 1] + 1 ? run + 1 : 1;
    if (run > longest) longest = run;
  }
  // Anchor at today if today has a log, else at the latest logged day (mirrors the Rust).
  const countsFromToday = uniq.includes(today);
  const anchorIdx = countsFromToday ? today : (uniq.length ? uniq[uniq.length - 1] : null);
  let current = 0;
  if (anchorIdx !== null) {
    const set = new Set(uniq);
    let d = anchorIdx;
    while (set.has(d)) { current++; d--; }
  }

  const overall = {
    total_completions: mine.length,
    total_points: totalPoints,
    active_chores: myChores.filter((c) => c.active !== 0).length,
    first_logged: firstIdx !== null ? idxToYmd(firstIdx) : null,
    last_logged: lastIdx !== null ? idxToYmd(lastIdx) : null,
    days_span: firstIdx !== null && lastIdx !== null ? lastIdx - firstIdx + 1 : 0,
    unit_note: "points are a plain per-chore tally chosen at setup, never a grade",
  };
  const period = {
    from: idxToYmd(from), to: idxToYmd(today),
    completions: inWin.length,
    points: inWin.reduce((a, c) => a + (c.points || 0), 0),
    active_days: winDays.size,
    distinct_chores: new Set(inWin.map((c) => c.chore_id)).size,
  };
  const streak = {
    current_days: current,
    anchor_day: anchorIdx !== null ? idxToYmd(anchorIdx) : null,
    counts_from_today: countsFromToday,
    longest_days: longest,
  };

  // ── headline facts — same five rules, same order, same wording as the Rust ─────
  const facts: HeadlineFact[] = [];
  if (period.completions > 0) {
    facts.push({ kind: "period", tone: "neutral", text:
      `In the last ${window} day${s(window)}, ${period.completions} chore${s(period.completions)} ` +
      `${period.completions === 1 ? "was" : "were"} logged across ${period.active_days} day${s(period.active_days)}, ` +
      `for ${period.points} point${s(period.points)}.` });
  }
  const top = per_chore.find((c) => c.total_count > 0);
  if (top) {
    const inw = top.period_count > 0 ? ` (${top.period_count} this period)` : "";
    facts.push({ kind: "top", tone: "active", text:
      `“${top.name}” is the most-logged chore — ${top.total_count} time${s(top.total_count)}${inw}, ` +
      `${top.total_points} point${s(top.total_points)} in total.` });
  }
  const quiet = per_chore.filter((c) => c.active && c.period_count === 0)
    .sort((a, b) => a.total_count - b.total_count || a.name.toLowerCase().localeCompare(b.name.toLowerCase()))[0];
  if (quiet) {
    const last = quiet.last_done_on ? ` (last logged ${quiet.last_done_on})` : " (never logged)";
    facts.push({ kind: "least", tone: "quiet", text:
      `“${quiet.name}” has no completions in the last ${window} day${s(window)}${last}.` });
  }
  if (streak.current_days >= 2) {
    facts.push({ kind: "streak", tone: "active", text:
      `A chore has been logged ${streak.current_days} day${s(streak.current_days)} in a row` +
      `${streak.counts_from_today ? ", including today" : ""}.` });
  } else if (streak.longest_days >= 2) {
    facts.push({ kind: "streak", tone: "neutral", text:
      `The longest run is ${streak.longest_days} day${s(streak.longest_days)} in a row with a chore logged.` });
  }
  if (overall.total_completions >= 2 && overall.days_span > 0) {
    facts.push({ kind: "span", tone: "neutral", text:
      `${overall.total_completions} chores logged in all, ${overall.total_points} point${s(overall.total_points)}, ` +
      `across ${overall.days_span} day${s(overall.days_span)} of records.` });
  }

  return {
    schema: SCHEMA,
    generated_for: todayYmd.slice(0, 10),
    member_name: member.name,
    period_days: window,
    overall, period, per_chore, streak,
    headline_facts: facts,
  };
}
